import { fetcher, fetcherConfig } from '../../lib/api-fetcher';
import { normalizeVersionedPath } from '@repo/api-client';
import { getAccessToken } from '../../lib/session';
import type {
  ReportDefinitionListItem,
  ReportDefinitionDetail,
  ReportRun,
  ReportSchedule,
  PaginatedResponse,
  ReportFormat,
  ReportCategory,
} from './types';



const BASE = '/staff/v1/reports';

export function listDefinitions(params: {
  page: number;
  limit: number;
  category?: ReportCategory;
}): Promise<PaginatedResponse<ReportDefinitionListItem>> {
  const qs = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    ...(params.category ? { category: params.category } : {}),
  });
  return fetcher.get<PaginatedResponse<ReportDefinitionListItem>>(
    `${BASE}/definitions?${qs}`,
  );
}

export function getDefinition(code: string): Promise<ReportDefinitionDetail> {
  return fetcher.get<ReportDefinitionDetail>(`${BASE}/definitions/${code}`);
}

export function runReport(body: {
  reportCode: string;
  parameters: Record<string, unknown>;
  format: ReportFormat;
}): Promise<ReportRun> {
  return fetcher.post<ReportRun>(`${BASE}/run`, body);
}

export function listRuns(params: { page: number; limit: number }): Promise<PaginatedResponse<ReportRun>> {
  const qs = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  return fetcher.get<PaginatedResponse<ReportRun>>(`${BASE}/runs?${qs}`);
}

export function getRun(id: string): Promise<ReportRun> {
  return fetcher.get<ReportRun>(`${BASE}/runs/${id}`);
}

export async function downloadRun(id: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${fetcherConfig.baseUrl}${normalizeVersionedPath(`${BASE}/runs/${id}/download`)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    redirect: 'follow',
  });

  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const json = (await res.json()) as Record<string, unknown>;
    const data = (json.data ?? json) as Record<string, unknown>;
    const url = (data.url ?? json.url) as string | undefined;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export function createSchedule(body: {
  reportCode: string;
  parameters: Record<string, unknown>;
  format: ReportFormat;
  cronExpression: string;
  recipients: Array<{ userId: string; email: string }>;
}): Promise<ReportSchedule> {
  return fetcher.post<ReportSchedule>(`${BASE}/schedules`, body);
}

export function listSchedules(params: {
  page: number;
  limit: number;
}): Promise<PaginatedResponse<ReportSchedule>> {
  const qs = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
  });
  return fetcher.get<PaginatedResponse<ReportSchedule>>(`${BASE}/schedules?${qs}`);
}

export function deleteSchedule(id: string): Promise<void> {
  return fetcher.delete<void>(`${BASE}/schedules/${id}`);
}

export function viewReport(
  code: string,
  params: Record<string, unknown>,
  pagination: { page: number; limit: number },
): Promise<PaginatedResponse<any>> {
  const qs = new URLSearchParams({
    page: String(pagination.page),
    limit: String(pagination.limit),
    ...Object.entries(params).reduce((acc, [k, v]) => {
      if (v !== undefined && v !== null) {
        acc[k] = String(v);
      }
      return acc;
    }, {} as Record<string, string>),
  });
  return fetcher.get<PaginatedResponse<any>>(`${BASE}/${code}/view?${qs}`);
}
